import React, { ChangeEvent, forwardRef, useRef } from 'react';

import cx from 'clsx';

import { SelectOption } from '../../../types';
import { RadioProps } from '../../Radio/Radio';
import { Checkbox } from '../Checkbox';

import styles from '../Checkbox.module.scss';

export type CheckboxGroupProps = Omit<RadioProps, 'onChange' | 'defaultValue' | 'value' | 'formStateField'> & {
  name: string;
  defaultValue?: string[];
  /**
   * Provided by React-hook-form if inside RHF context
   */
  formStateValue?: Array<string>;

  options: SelectOption[];
  /**
   * Event that triggers on checkbox checking
   * @param values is array of checked values
   */
  onChange?: (values: string[], e: ChangeEvent<HTMLInputElement> | undefined) => void;
  className?: string;
};

/**
 * Compact solution that allows to create a checkbox group
 * without passing the ref to each and every component separately
 */
export const CheckboxGroup = forwardRef<HTMLInputElement, CheckboxGroupProps>(
  ({ name, id, className, defaultValue = [], formStateValue, onChange, options, ...radioProps }, ref) => {
    const checked = useRef<Array<string>>(defaultValue || []);

    return (
      <div className={cx(styles['checkbox-group'], className)} data-testid="checkbox-group-component">
        {options.map(({ label, value }) => (
          <Checkbox
            {...radioProps}
            id={id}
            key={value}
            value={value}
            ref={ref}
            name={name}
            checked={(typeof formStateValue !== 'undefined' ? formStateValue || [] : checked.current).includes(value)}
            onChange={(val, e) => {
              let newChecked: string[] = [];

              if (val) {
                // Rendered inside React-hook-form context
                if (formStateValue) {
                  const formFieldValue = formStateValue || [];

                  newChecked = formFieldValue.includes(val)
                    ? formFieldValue.filter((el: string) => el !== val)
                    : [...formFieldValue, val];
                } else {
                  newChecked = checked.current.includes(val)
                    ? checked.current.filter((el) => el !== val)
                    : [...checked.current, val];

                  checked.current = newChecked;
                }
              }

              if (onChange) {
                onChange(newChecked, e);
              }
            }}
          >
            {label}
          </Checkbox>
        ))}
      </div>
    );
  }
);

CheckboxGroup.displayName = 'CheckboxGroup';
